import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Box, Building2, Layers, Printer, Shirt, Sticker } from "lucide-react";
import { AnimatedLogo } from "../components/site/AnimatedLogo";
import { useSite } from "../context/SiteContext";
import { imgUrl } from "../lib/api";
import { playThunder } from "../lib/thunder";

const STRIP = [
  { icon: Shirt, label: "Custom Apparel" }, { icon: Layers, label: "DTF Transfers" }, { icon: Printer, label: "UV Printing" },
  { icon: Sticker, label: "Stickers" }, { icon: Building2, label: "Business Branding" }, { icon: Box, label: "Bulk Orders" },
];
const STEPS = [
  ["Submit details", "Choose your item type, colors, quantities, sizes, and upload your logo or design."],
  ["We review it", "ASAP & Co. checks the order details, artwork, placement, and pricing."],
  ["Invoice & print", "You'll receive an invoice or payment link before production begins."],
];

const useReplayKey = (enabled) => {
  const [key, setKey] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let away = false;
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * 0.6) away = true;
      else if (away && window.scrollY < 40) { away = false; setKey((k) => k + 1); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]);
  return key;
};

const d = (sec) => ({ animationDelay: `calc(var(--spd, 1) * ${sec}s)` });

const Hero = ({ s }) => {
  const replay = useReplayKey(s.hero_anim_replay !== false);
  useEffect(() => {
    if (!replay) return undefined;
    const t = setTimeout(playThunder, 120);
    return () => clearTimeout(t);
  }, [replay]);
  return (
  <section className="relative overflow-hidden" style={{ "--spd": s.hero_anim_speed || 0.8 }} data-testid="home-hero">
    <img src={imgUrl(s.hero_image || "/assets/hero-shop-bg.jpg")} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-[70%_center]" data-testid="hero-background-image" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/10" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
    <div key={`f${replay}`} className="hero-flash pointer-events-none absolute inset-0" aria-hidden="true" data-testid="hero-lightning-flash" />
    <div key={`c${replay}`} className="relative mx-auto flex min-h-[calc(100vh-12rem)] max-w-7xl items-center px-6 py-14 lg:py-20">
      <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-start sm:text-left lg:max-w-3xl">
        <AnimatedLogo className="w-40 shrink-0 sm:w-48 lg:w-56" entrance="logo-flash" testId="hero-animated-logo" />
        <div>
          <h1 className="font-script leading-[0.95]">
            <span className="drop-in block text-5xl text-white sm:text-6xl lg:text-7xl" style={d(0.9)}>{s.hero_script_1}</span>
            <span className="bounce-in block" style={d(1.2)}><span className="block -rotate-3 text-4xl text-[#E5202A] sm:text-5xl lg:text-6xl">{s.hero_script_2}</span></span>
          </h1>
          <p className="flash-text mt-5 font-display text-2xl font-bold leading-tight text-white md:text-3xl" style={d(1.55)}>{s.hero_subtitle}</p>
          <div className="slam-in brush-strip mt-5 inline-block px-6 py-2 font-display text-lg font-bold italic text-white md:text-xl" style={d(1.85)}>{s.hero_strip}</div>
          <div className="bounce-in mt-7 flex flex-wrap justify-center gap-3 sm:justify-start" style={d(2.1)}>
            <Link to="/quote" className="btn-red" data-testid="hero-request-quote-button">Request a Quote <ArrowRight className="h-5 w-5" /></Link>
            <Link to="/work" className="btn-ghost" data-testid="hero-view-work-button">View Our Work</Link>
          </div>
        </div>
      </div>
    </div>
    <div className="relative border-y border-white/10 bg-black/80 backdrop-blur-md">
      <div className="mx-auto grid max-w-7xl grid-cols-3 divide-white/10 px-2 md:grid-cols-6 md:divide-x">
        {STRIP.map(({ icon: Icon, label }, i) => (
          <Link key={label} to="/services" className="group flex flex-col items-center gap-2 px-2 py-6 text-center" data-testid={`service-strip-${i}`}>
            <Icon className="h-9 w-9 text-[#E5202A] transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110" strokeWidth={1.6} />
            <span className="font-display text-sm font-bold uppercase leading-tight text-white md:text-base">{label}</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

export default function Home() {
  const { settings: s = {}, services = [], gallery = [] } = useSite();
  return (
    <>
      <Hero s={s} />
      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="home-services">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><span className="chip">What we offer</span><h2 className="outline-title mt-4 text-5xl md:text-6xl">Services</h2></div>
          <Link to="/services" className="btn-ghost !py-2" data-testid="home-all-services-link">All Services</Link>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 6).map((sv) => (
            <Link to="/order" key={sv.id} className="group panel overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:border-[#E5202A]/50" data-testid={`home-service-card-${sv.id}`}>
              <img src={imgUrl(sv.image)} alt={sv.title} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="p-5"><h3 className="red-title text-2xl">{sv.title}</h3><p className="mt-2 text-sm text-slate-400">{sv.description}</p></div>
            </Link>
          ))}
        </div>
      </section>
      <section className="border-y border-white/10 bg-black/50 py-20" data-testid="home-how-it-works">
        <div className="mx-auto max-w-7xl px-6">
          <span className="chip">Start your order</span>
          <h2 className="mt-4 font-display text-4xl font-black uppercase text-white md:text-5xl">How it works</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {(s.home_steps?.length ? s.home_steps.map((x) => [x.title, x.text]) : STEPS).map(([t, d], i) => (
              <div key={t} className="panel p-7">
                <span className="font-display text-6xl font-black text-[#E5202A]">0{i + 1}</span>
                <h3 className="mt-2 font-display text-2xl font-bold uppercase">{t}</h3>
                <p className="mt-2 text-sm text-slate-400">{d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/order" className="btn-red" data-testid="home-start-order-button">Start Custom Order <ArrowRight className="h-5 w-5" /></Link>
            <Link to="/design" className="btn-ghost" data-testid="home-design-studio-button">Preview Your Design</Link>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="home-work">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="outline-title text-5xl md:text-6xl">Our Work</h2>
          <Link to="/work" className="btn-ghost !py-2" data-testid="home-view-all-work-link">View All</Link>
        </div>
        <div className="mt-10 columns-2 gap-4 md:columns-4">
          {gallery.slice(0, 8).map((g) => (
            <img key={g.id} src={imgUrl(g.image)} alt={g.caption} loading="lazy" className="mb-4 w-full rounded-xl border border-white/10 transition-transform duration-300 hover:scale-[1.02]" />
          ))}
        </div>
      </section>
      <section className="mx-auto mb-20 max-w-5xl px-6">
        <div className="panel border-[#E5202A]/60 bg-gradient-to-r from-[#3a0a0d] via-[#0E1018] to-[#0b1a4a] p-10 text-center">
          <h2 className="red-title text-3xl md:text-4xl">{s.bulk_title}</h2>
          <p className="mt-2 text-lg font-bold text-white">{s.bulk_text}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/pricing" className="btn-red" data-testid="home-pricing-button">See Pricing</Link>
            <Link to="/quote" className="btn-ghost" data-testid="home-quote-button">Request a Quote</Link>
          </div>
        </div>
      </section>
    </>
  );
}
